import { useEffect } from "react";
import { createPortal } from "react-dom";
import { RiCloseLine, RiPencilLine } from "react-icons/ri";
import {
  InlineSurveyCard,
  type InlineSurveyProps,
} from "./inlineSurvey.ui";

/**
 * 從「約每 N 天」主動更新用氣習慣：置中 dialog + 既有一次一題問卷。
 * 送出仍走 POST /customer/gas-survey（由 onComplete 處理）。
 */
export const SurveyUpdateModal = ({
  open,
  onClose,
  busy = false,
  ...surveyProps
}: {
  open: boolean;
  onClose: () => void;
  /** 送出中不可關閉，避免中斷 POST */
  busy?: boolean;
} & Omit<InlineSurveyProps, "spotlight">) => {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open || busy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="survey-update-title"
      className="survey-update-root"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 16px",
        boxSizing: "border-box",
      }}>
      <style>{`
        .survey-update-backdrop {
          animation: surveyUpdateBackdropIn 0.28s ease both;
        }
        .survey-update-dialog {
          animation: surveyUpdateDialogIn 0.34s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .survey-update-header-in {
          animation: surveyUpdateRise 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.06s both;
        }
        .survey-update-body-in {
          animation: surveyUpdateRise 0.42s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
        }
        .survey-update-icon-pop {
          animation: surveyUpdateIconPop 0.45s cubic-bezier(0.2, 1.2, 0.4, 1) 0.1s both;
        }
        @keyframes surveyUpdateBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes surveyUpdateDialogIn {
          from { transform: translateY(16px) scale(0.96); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes surveyUpdateRise {
          from { transform: translateY(8px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes surveyUpdateIconPop {
          from { transform: scale(0.7); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .survey-update-backdrop,
          .survey-update-dialog,
          .survey-update-header-in,
          .survey-update-body-in,
          .survey-update-icon-pop {
            animation: none !important;
          }
        }
      `}</style>

      <button
        type="button"
        aria-label="關閉"
        disabled={busy}
        className="survey-update-backdrop"
        onClick={() => {
          if (!busy) onClose();
        }}
        style={{
          position: "absolute",
          inset: 0,
          border: "none",
          background: "rgba(15, 23, 42, 0.46)",
          cursor: busy ? "wait" : "pointer",
          padding: 0,
        }}
      />

      <div
        className="survey-update-dialog"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 392,
          maxHeight: "min(78vh, 640px)",
          display: "flex",
          flexDirection: "column",
          background: "#F7F9FC",
          borderRadius: 22,
          boxShadow:
            "0 28px 56px -24px rgba(15,23,42,0.42), 0 0 0 1px rgba(255,255,255,0.65) inset",
          overflow: "hidden",
        }}>
        {/* soft top accent */}
        <div
          aria-hidden
          style={{
            height: 3,
            background: "linear-gradient(90deg, #42A5F5 0%, #1E88E5 55%, #22D3EE 100%)",
            flexShrink: 0,
          }}
        />

        <div
          className="survey-update-header-in"
          style={{
            flexShrink: 0,
            padding: "16px 16px 14px",
            background:
              "linear-gradient(180deg, #FFFFFF 0%, rgba(247,249,252,0.92) 100%)",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              className="survey-update-icon-pop"
              style={{
                width: 42,
                height: 42,
                borderRadius: 14,
                background: "linear-gradient(145deg, #E3F2FD 0%, #BBDEFB 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 6px 14px -8px rgba(30,136,229,0.55)",
              }}>
              <RiPencilLine size={19} color="#1565C0" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                id="survey-update-title"
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#1F2937",
                  lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                }}>
                更新問卷
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#8B909C",
                  marginTop: 3,
                  lineHeight: 1.4,
                }}>
                調整後會重新估算叫桶週期
              </div>
            </div>
            <button
              type="button"
              aria-label="關閉問卷"
              disabled={busy}
              onClick={() => {
                if (!busy) onClose();
              }}
              style={{
                width: 34,
                height: 34,
                borderRadius: 11,
                border: "none",
                background: "rgba(28,33,40,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: busy ? "wait" : "pointer",
                flexShrink: 0,
                opacity: busy ? 0.55 : 1,
                transition: "background 0.15s ease, transform 0.15s ease",
              }}>
              <RiCloseLine size={18} color="#5B6472" />
            </button>
          </div>
        </div>

        <div
          className="survey-update-body-in"
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "4px 14px 16px",
          }}>
          <InlineSurveyCard
            key={open ? "survey-update-open" : "survey-update-closed"}
            spotlight
            reason="refresh"
            {...surveyProps}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};
